import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Mail, Phone, MapPin } from 'lucide-react';

// Composant de séparation progressive Orbloom
const OrbloomSeparation = () => {
  const [scrollPos, setScrollPos] = useState(0);
  const containerRef = useRef(null);
  const maxScroll = 800;

  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        // Si la section est visible à l'écran
        if (rect.top <= 0 && rect.bottom > window.innerHeight) {
          const scrolled = Math.abs(rect.top);
          setScrollPos(Math.min(scrolled, maxScroll));
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Calcul du pourcentage d'animation (0 à 1)
  const progress = Math.min(scrollPos / maxScroll, 1);

  // Positions finales des mots
  const orbFinalX = -window.innerWidth * 0.33;
  const bloomFinalX = 0;
  const loomFinalX = window.innerWidth * 0.33;

  return (
    <section
      ref={containerRef}
      className="h-[800px] relative bg-gradient-to-br from-gray-50 to-white snap-start snap-always"
    >
      <div className="sticky top-0 h-screen flex items-center justify-center overflow-hidden">
        {/* Phase 1: Mot complet (0-30%) */}
        <h1
          className="absolute text-9xl font-heading font-bold transition-opacity duration-300"
          style={{
            color: '#131661',
            opacity: progress < 0.3 ? 1 : 0,
          }}
        >
          Orbloom
        </h1>

        {/* Phase 2: Séparation des mots (30-70%) */}
        <div
          className="absolute text-7xl font-heading font-bold transition-all duration-300"
          style={{
            color: '#0070DF',
            opacity: progress >= 0.3 && progress < 0.7 ? 1 : 0,
            transform: `translateX(${progress >= 0.3 && progress < 0.7 ? orbFinalX * ((progress - 0.3) / 0.4) : 0}px)`,
          }}
        >
          Orb
        </div>

        <div
          className="absolute text-7xl font-heading font-bold transition-all duration-300"
          style={{
            color: '#3CAF8D',
            opacity: progress >= 0.3 && progress < 0.7 ? 1 : 0,
            transform: `translateX(${progress >= 0.3 && progress < 0.7 ? bloomFinalX * ((progress - 0.3) / 0.4) : 0}px)`,
          }}
        >
          Bloom
        </div>

        <div
          className="absolute text-7xl font-heading font-bold transition-all duration-300"
          style={{
            color: '#FED040',
            opacity: progress >= 0.3 && progress < 0.7 ? 1 : 0,
            transform: `translateX(${progress >= 0.3 && progress < 0.7 ? loomFinalX * ((progress - 0.3) / 0.4) : 0}px)`,
          }}
        >
          Loom
        </div>

        {/* Phase 3: Colonnes avec descriptions (70-100%) */}
        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-6xl px-8 transition-opacity duration-500"
          style={{
            opacity: progress >= 0.7 ? 1 : 0,
          }}
        >
          {/* Orb */}
          <div className="text-center">
            <div
              className="w-32 h-32 mx-auto mb-6 rounded-full shadow-xl transition-all duration-500"
              style={{
                background: 'radial-gradient(circle at 30% 30%, #0070DFdd, #0070DF)',
                transform: `scale(${progress >= 0.7 ? Math.min((progress - 0.7) / 0.15, 1) : 0}) translateX(${progress >= 0.7 ? 200 * (1 - Math.min((progress - 0.7) / 0.15, 1)) : 200}px)`,
              }}
            />
            <h3
              className="text-4xl font-heading font-bold mb-4 transition-all duration-500"
              style={{
                color: '#0070DF',
                opacity: progress >= 0.8 ? Math.min((progress - 0.8) / 0.1, 1) : 0,
                transform: `translateY(${progress >= 0.8 ? 50 * (1 - Math.min((progress - 0.8) / 0.1, 1)) : 50}px)`,
              }}
            >
              Orb
            </h3>
            <p
              className="text-lg text-gray-600 transition-all duration-500"
              style={{
                opacity: progress >= 0.85 ? Math.min((progress - 0.85) / 0.1, 1) : 0,
                transform: `translateY(${progress >= 0.85 ? 30 * (1 - Math.min((progress - 0.85) / 0.1, 1)) : 30}px)`,
              }}
            >
              La sphère qui englobe<br />
              Un écosystème complet<br />
              et interconnecté
            </p>
          </div>

          {/* Bloom */}
          <div className="text-center">
            <div
              className="w-32 h-32 mx-auto mb-6 rounded-full shadow-xl transition-all duration-500"
              style={{
                background: 'radial-gradient(circle at 30% 30%, #3CAF8Ddd, #3CAF8D)',
                transform: `scale(${progress >= 0.75 ? Math.min((progress - 0.75) / 0.15, 1) : 0})`,
              }}
            />
            <h3
              className="text-4xl font-heading font-bold mb-4 transition-all duration-500"
              style={{
                color: '#3CAF8D',
                opacity: progress >= 0.85 ? Math.min((progress - 0.85) / 0.1, 1) : 0,
                transform: `translateY(${progress >= 0.85 ? 50 * (1 - Math.min((progress - 0.85) / 0.1, 1)) : 50}px)`,
              }}
            >
              Bloom
            </h3>
            <p
              className="text-lg text-gray-600 transition-all duration-500"
              style={{
                opacity: progress >= 0.9 ? Math.min((progress - 0.9) / 0.1, 1) : 0,
                transform: `translateY(${progress >= 0.9 ? 30 * (1 - Math.min((progress - 0.9) / 0.1, 1)) : 30}px)`,
              }}
            >
              L'épanouissement<br />
              Des compétences<br />
              et des talents
            </p>
          </div>

          {/* Loom */}
          <div className="text-center">
            <div
              className="w-32 h-32 mx-auto mb-6 rounded-full shadow-xl transition-all duration-500"
              style={{
                background: 'radial-gradient(circle at 30% 30%, #FED040dd, #FED040)',
                transform: `scale(${progress >= 0.7 ? Math.min((progress - 0.7) / 0.15, 1) : 0}) translateX(${progress >= 0.7 ? -200 * (1 - Math.min((progress - 0.7) / 0.15, 1)) : -200}px)`,
              }}
            />
            <h3
              className="text-4xl font-heading font-bold mb-4 transition-all duration-500"
              style={{
                color: '#FED040',
                opacity: progress >= 0.8 ? Math.min((progress - 0.8) / 0.1, 1) : 0,
                transform: `translateY(${progress >= 0.8 ? 50 * (1 - Math.min((progress - 0.8) / 0.1, 1)) : 50}px)`,
              }}
            >
              Loom
            </h3>
            <p
              className="text-lg text-gray-600 transition-all duration-500"
              style={{
                opacity: progress >= 0.85 ? Math.min((progress - 0.85) / 0.1, 1) : 0,
                transform: `translateY(${progress >= 0.85 ? 30 * (1 - Math.min((progress - 0.85) / 0.1, 1)) : 30}px)`,
              }}
            >
              Le métier à tisser<br />
              Qui unit et entrelace<br />
              les parcours
            </p>
          </div>
        </div>

        {/* Message final */}
        <div
          className="absolute bottom-16 text-center transition-opacity duration-500"
          style={{
            opacity: progress >= 0.9 ? (progress - 0.9) / 0.1 : 0,
          }}
        >
          <p className="text-2xl text-gray-500 italic">
            Un monde où les compétences s'épanouissent et se tissent ensemble
          </p>
        </div>

        {/* Indicateur de scroll (optionnel, pour debug) */}
        {process.env.NODE_ENV === 'development' && (
          <div className="absolute top-4 right-4 bg-black/50 text-white px-3 py-1 rounded text-sm">
            Scroll: {Math.round(scrollPos)}px / {maxScroll}px ({Math.round(progress * 100)}%)
          </div>
        )}
      </div>
    </section>
  );
};

const OrbloomPresentation = () => {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [activeCarousels, setActiveCarousels] = useState({});

  // Définition des projets avec leurs informations
  const projects = [
    {
      id: 'metier',
      name: 'Métier',
      description: "L'application centrale où se croisent et s'entrelacent toutes les compétences",
      color: '#0070DF', // Primary
      route: '/projects',
      images: ['/screenshots/metier-1.png', '/screenshots/metier-2.png'],
      angle: 0
    },
    {
      id: 'chaine',
      name: 'Chaîne',
      description: "L'import des référentiels RNCP, ROME, et autres sources qui forment la base",
      color: '#3CAF8D', // Secondary
      route: '/import-export',
      images: ['/screenshots/chaine-1.png'],
      angle: 45
    },
    {
      id: 'navette',
      name: 'Navette',
      description: "L'IA qui traverse les compétences, créant des rapprochements pertinents",
      color: '#FED040', // Accent
      route: '/correspondances',
      images: ['/screenshots/navette-1.png'],
      angle: 90
    },
    {
      id: 'trame',
      name: 'Trame',
      description: "Datavisualisation élégante qui révèle les motifs cachés",
      color: '#0070DF',
      route: '/rncp-visualization',
      images: ['/screenshots/trame-1.png'],
      angle: 135
    },
    {
      id: 'aiguille',
      name: 'Aiguille',
      description: "Le rapprochement fin entre compétences acquises et recherchées",
      color: '#3CAF8D',
      route: '/rncp-search',
      images: ['/screenshots/aiguille-1.png'],
      angle: 180
    },
    {
      id: 'tapis',
      name: 'Tapis',
      description: "Le résultat final, où chaque fil trouve sa place",
      color: '#FED040',
      route: '/projects',
      images: ['/screenshots/tapis-1.png'],
      angle: 225
    },
    {
      id: 'echeveau',
      name: 'Écheveau',
      description: "VAE et mobilité : démêler le passé pour tisser l'avenir",
      color: '#0070DF',
      route: '/competences',
      images: ['/screenshots/echeveau-1.png'],
      angle: 270
    },
    {
      id: 'fusette',
      name: 'Fusette',
      description: "Modules IA générés automatiquement",
      color: '#3CAF8D',
      route: '/settings',
      images: ['/screenshots/fusette-1.png'],
      angle: 315
    }
  ];

  // Gestion du carousel pour chaque projet
  const handleCarouselPrev = (projectId, imagesLength) => {
    setActiveCarousels(prev => ({
      ...prev,
      [projectId]: ((prev[projectId] || 0) - 1 + imagesLength) % imagesLength
    }));
  };

  const handleCarouselNext = (projectId, imagesLength) => {
    setActiveCarousels(prev => ({
      ...prev,
      [projectId]: ((prev[projectId] || 0) + 1) % imagesLength
    }));
  };

  // Calcul des hauteurs de sections
  const SECTION_HEIGHT = 1000; // Hauteur standard d'une section
  const INTRO_HEIGHT = SECTION_HEIGHT;
  const ORBIT_HEIGHT = SECTION_HEIGHT;
  const PROJECTS_HEIGHT = SECTION_HEIGHT * projects.length;
  const ORBLOOM_START = INTRO_HEIGHT + ORBIT_HEIGHT + PROJECTS_HEIGHT;
  const ORBLOOM_HEIGHT = 1200; // 1200px pour l'animation complète
  const CONTACT_START = ORBLOOM_START + ORBLOOM_HEIGHT;
  const CONTACT_HEIGHT = SECTION_HEIGHT;

  return (
    <div
      ref={containerRef}
      className="bg-gray-50"
      style={{ height: `${INTRO_HEIGHT + ORBIT_HEIGHT + PROJECTS_HEIGHT + ORBLOOM_HEIGHT + CONTACT_HEIGHT}px` }}
    >
      {/* Section 1: Introduction - Orbloom avec orbs flottants */}
      <section className="h-screen snap-start snap-always flex items-center justify-center relative overflow-hidden">
        {/* Background orbs animés */}
        <div className="absolute inset-0">
          {[...Array(12)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full"
              style={{
                width: Math.random() * 200 + 100,
                height: Math.random() * 200 + 100,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                background: `radial-gradient(circle, ${
                  ['#0070DF', '#3CAF8D', '#FED040'][i % 3]
                }20, transparent)`,
              }}
              animate={{
                x: [0, Math.random() * 100 - 50, 0],
                y: [0, Math.random() * 100 - 50, 0],
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 10 + Math.random() * 10,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          ))}
        </div>

        {/* Contenu principal */}
        <div className="relative z-10 text-center px-8 max-w-5xl">
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1, type: "spring", stiffness: 100 }}
          >
            <h1 className="text-8xl font-heading font-bold mb-6" style={{ color: '#131661' }}>
              Orbloom
            </h1>
          </motion.div>

          <motion.p
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="text-3xl text-gray-600 font-light"
          >
            L'atelier numérique des compétences
          </motion.p>

          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="mt-8 text-lg text-gray-500"
          >
            Là où chaque fil trouve sa place dans la trame de l'avenir professionnel
          </motion.div>

          {/* Scroll hint */}
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="absolute bottom-12 left-1/2 transform -translate-x-1/2 text-gray-400 text-sm"
          >
            Faites défiler pour découvrir
          </motion.div>
        </div>
      </section>

      {/* Section 2: Explosion des projets autour d'Orbloom */}
      <section className="h-screen snap-start snap-always flex items-center justify-center relative bg-white">
        <div className="relative w-full h-full flex items-center justify-center" style={{ overflow: 'visible' }}>
          {/* Nom central Orbloom */}
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, type: "spring" }}
            className="absolute z-20"
          >
            <div className="text-6xl font-heading font-bold" style={{ color: '#131661' }}>
              Orbloom
            </div>
          </motion.div>

          {/* Conteneur rotatif pour les projets */}
          <motion.div
            className="absolute w-full h-full"
            animate={{ rotate: 360 }}
            transition={{
              duration: 60,
              repeat: Infinity,
              ease: "linear"
            }}
          >
            {/* Projets en orbite */}
            {projects.map((project, index) => {
              const radius = 330; // Rayon de l'orbite (réduit de 50px)
              const angle = (index * 360) / projects.length;
              const radian = (angle * Math.PI) / 180;
              const x = Math.cos(radian) * radius;
              const y = Math.sin(radian) * radius;

              return (
                <motion.div
                  key={project.id}
                  initial={{ scale: 0, x: 0, y: 0 }}
                  whileInView={{
                    scale: 1,
                    x: x,
                    y: y,
                  }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.8,
                    delay: 0.3 + index * 0.1,
                    type: "spring",
                    stiffness: 100
                  }}
                  className="absolute cursor-pointer z-30 left-1/2 top-1/2"
                  style={{ transform: 'translate(-50%, -50%)' }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    window.location.href = project.route;
                  }}
                >
                  {/* Conteneur contre-rotatif pour garder l'orb droit */}
                  <motion.div
                    animate={{ rotate: -360 }}
                    transition={{
                      duration: 60,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                    whileHover={{ scale: 1.1 }}
                    className="relative"
                  >
                    {/* Orb du projet */}
                    <div
                      className="w-24 h-24 rounded-full shadow-lg flex items-center justify-center text-white font-semibold text-sm text-center p-4"
                      style={{
                        background: `radial-gradient(circle at 30% 30%, ${project.color}dd, ${project.color})`,
                      }}
                    >
                      {project.name}
                    </div>

                    {/* Satellites autour de chaque projet avec effet 3D de passage devant/derrière */}
                    {[0, 1, 2].map((satIndex) => {
                      const baseAngle = (satIndex * 360) / 3 + index * 30; // Décalage selon le projet
                      const satRadius = 50;
                      const duration = 8 + satIndex * 2;

                      return (
                        <motion.div
                          key={satIndex}
                          className="absolute rounded-full pointer-events-none"
                          style={{
                            left: '50%',
                            top: '50%',
                            marginLeft: '-6px',
                            marginTop: '-6px',
                          }}
                          animate={{
                            x: Array.from({ length: 360 }, (_, deg) =>
                              Math.cos(((baseAngle + deg) * Math.PI) / 180) * satRadius
                            ),
                            y: Array.from({ length: 360 }, (_, deg) =>
                              Math.sin(((baseAngle + deg) * Math.PI) / 180) * satRadius
                            ),
                            // Scale simule la profondeur : grand devant, petit derrière
                            scale: Array.from({ length: 360 }, (_, deg) => {
                              const angle = (baseAngle + deg) % 360;
                              // Plus grand quand il passe devant (90-270°), plus petit derrière (270-90°)
                              return angle > 90 && angle < 270 ? 1.5 : 0.6;
                            }),
                            // Opacité pour renforcer l'effet de profondeur
                            opacity: Array.from({ length: 360 }, (_, deg) => {
                              const angle = (baseAngle + deg) % 360;
                              return angle > 90 && angle < 270 ? 0.9 : 0.4;
                            }),
                          }}
                          transition={{
                            duration: duration,
                            repeat: Infinity,
                            ease: "linear",
                          }}
                        >
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{
                              background: `${project.color}`,
                              boxShadow: `0 0 10px ${project.color}80`,
                            }}
                          />
                        </motion.div>
                      );
                    })}
                  </motion.div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Sections 3-10: Détails de chaque projet avec carousel */}
      {projects.map((project, idx) => (
        <section
          key={project.id}
          className="h-screen snap-start snap-always flex items-center justify-center relative"
          style={{ backgroundColor: idx % 2 === 0 ? '#FFFFFF' : '#F6F7F6' }}
        >
          <div className="max-w-6xl w-full px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Colonne gauche: Texte */}
            <motion.div
              initial={{ x: -100, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div
                className="w-20 h-20 rounded-full mb-6 shadow-md"
                style={{
                  background: `radial-gradient(circle at 30% 30%, ${project.color}dd, ${project.color})`,
                }}
              />
              <h2 className="text-5xl font-heading font-bold mb-4" style={{ color: '#131661' }}>
                {project.name}
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed mb-6">
                {project.description}
              </p>
              <button
                onClick={() => navigate(project.route)}
                className="px-6 py-3 rounded-lg text-white font-medium shadow-md hover:shadow-lg transition-all"
                style={{ backgroundColor: project.color }}
              >
                Découvrir le module
              </button>
            </motion.div>

            {/* Colonne droite: Carousel d'images */}
            <motion.div
              initial={{ x: 100, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="relative rounded-xl overflow-hidden shadow-lg bg-gray-100 w-[500px] h-[500px]">
                {project.images.length > 0 ? (
                  <>
                    <img
                      src={project.images[activeCarousels[project.id] || 0]}
                      alt={`${project.name} screenshot`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="500" height="500"%3E%3Crect fill="%23f0f0f0" width="500" height="500"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999" font-family="sans-serif" font-size="18"%3ECapture d\'écran%3C/text%3E%3C/svg%3E';
                      }}
                    />

                    {/* Flèches de navigation si plusieurs images */}
                    {project.images.length > 1 && (
                      <>
                        <button
                          onClick={() => handleCarouselPrev(project.id, project.images.length)}
                          className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full shadow-md flex items-center justify-center hover:bg-white transition-colors"
                        >
                          <ChevronLeft className="w-6 h-6 text-gray-700" />
                        </button>
                        <button
                          onClick={() => handleCarouselNext(project.id, project.images.length)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 rounded-full shadow-md flex items-center justify-center hover:bg-white transition-colors"
                        >
                          <ChevronRight className="w-6 h-6 text-gray-700" />
                        </button>

                        {/* Indicateurs */}
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                          {project.images.map((_, imgIdx) => (
                            <div
                              key={imgIdx}
                              className={`w-2 h-2 rounded-full transition-all ${
                                (activeCarousels[project.id] || 0) === imgIdx
                                  ? 'bg-white w-6'
                                  : 'bg-white/50'
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    Capture d'écran à venir
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </section>
      ))}

      {/* Section: Séparation progressive Orbloom */}
      <OrbloomSeparation />

      {/* Section finale: Contact */}
      <section className="h-screen snap-start snap-always flex items-center justify-center relative bg-white">
        <div className="max-w-4xl px-8 text-center">
          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-6xl font-heading font-bold mb-8" style={{ color: '#131661' }}>
              Contactez-nous
            </h2>
            <p className="text-2xl text-gray-600 mb-12">
              Prêt à tisser votre avenir professionnel avec Orbloom ?
            </p>
          </motion.div>

          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12"
          >
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                <Mail className="w-8 h-8" style={{ color: '#0070DF' }} />
              </div>
              <h4 className="font-semibold text-lg mb-2" style={{ color: '#131661' }}>Email</h4>
              <a href="mailto:contact@orbloom.fr" className="text-gray-600 hover:text-primary-600">
                contact@orbloom.fr
              </a>
            </div>

            <div className="flex flex-col items-center">
              <div className="w-16 h-16 rounded-full bg-secondary-100 flex items-center justify-center mb-4">
                <Phone className="w-8 h-8" style={{ color: '#3CAF8D' }} />
              </div>
              <h4 className="font-semibold text-lg mb-2" style={{ color: '#131661' }}>Téléphone</h4>
              <a href="tel:+33123456789" className="text-gray-600 hover:text-secondary-600">
                +33 1 23 45 67 89
              </a>
            </div>

            <div className="flex flex-col items-center">
              <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mb-4">
                <MapPin className="w-8 h-8" style={{ color: '#FED040' }} />
              </div>
              <h4 className="font-semibold text-lg mb-2" style={{ color: '#131661' }}>Adresse</h4>
              <p className="text-gray-600">
                Paris, France
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6, duration: 0.8 }}
          >
            <button
              onClick={() => window.location.href = '/login'}
              className="px-8 py-4 rounded-lg text-white font-semibold text-lg shadow-lg hover:shadow-xl transition-all"
              style={{ background: 'linear-gradient(135deg, #0070DF, #3CAF8D)' }}
            >
              Commencer maintenant
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default OrbloomPresentation;
